import { createStore, useStore } from 'zustand';

/**
 * Toast queue. Any code can call toast.success('Saved'); the ToastHost in the
 * shell renders the queue. Undo toasts stay longer and run the callback once.
 */
export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface ToastItem {
  id: string;
  tone: ToastTone;
  message: string;
  undo?: (() => void) | undefined;
  /** A named button (for example Klev's "Open" when an answer is ready elsewhere). */
  action?: ToastAction | undefined;
}

interface PushOpts {
  undo?: (() => void) | undefined;
  action?: ToastAction | undefined;
  durationMs?: number | undefined;
}

interface ToastState {
  items: ToastItem[];
}

export const toastStore = createStore<ToastState>(() => ({ items: [] }));

const timers = new Map<string, ReturnType<typeof setTimeout>>();

export function dismissToast(id: string): void {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.delete(id);
  toastStore.setState((s) => ({ items: s.items.filter((x) => x.id !== id) }));
}

function push(tone: ToastTone, message: string, opts?: PushOpts): string {
  const id = Math.random().toString(36).slice(2);
  const undo = opts?.undo;
  const action = opts?.action;
  toastStore.setState((s) => ({ items: [...s.items.slice(-3), { id, tone, message, undo, action }] }));
  const ms = opts?.durationMs ?? (action ? 9000 : undo ? 6000 : 3400);
  timers.set(
    id,
    setTimeout(() => {
      dismissToast(id);
    }, ms),
  );
  return id;
}

export const toast = {
  info: (message: string, opts?: PushOpts) => push('info', message, opts),
  success: (message: string, opts?: PushOpts) => push('success', message, opts),
  warning: (message: string, opts?: PushOpts) => push('warning', message, opts),
  danger: (message: string, opts?: PushOpts) => push('danger', message, opts),
  dismiss: dismissToast,
  clear(): void {
    for (const id of timers.keys()) dismissToast(id);
  },
};

export function useToasts(): ToastItem[] {
  return useStore(toastStore, (s) => s.items);
}
